import {
  IPrequalificationFieldError,
  PrequalificationFormType,
} from "../../../services/prequalification";

export type PrequalFormFieldName = Exclude<keyof PrequalificationFormType, "isAgreed">;

/**
 * Backend error key → the form field(s) to highlight. Keyed by both the
 * Scratch payload key and the form field name, so the field is found
 * whichever key the backend relays. The first field carries the message; any
 * others are only outlined and are cleared together.
 *
 * Keys not listed here (petAge, selectedPetTypeId, planValue, consents.*,
 * practiceUniqueId, requestedAmount, ...) aren't editable on the form.
 */
export const SERVER_ERROR_FIELD_MAP: Record<string, PrequalFormFieldName[]> = {
  firstName: ["firstName"],
  lastName: ["lastName"],
  email: ["email"],
  phoneNumber: ["phone"],
  phone: ["phone"],
  address1: ["street"],
  street: ["street"],
  city: ["city"],
  state: ["state"],
  // Scratch derives the state from the ZIP code, so a ZIP/state mismatch is
  // reported as `stateCode`; either field may be the one to correct.
  stateCode: ["zip", "state"],
  postalCode: ["zip"],
  zip: ["zip"],
  birthDate: ["dateOfBirth"],
  dateOfBirth: ["dateOfBirth"],
};

/**
 * Keeps only errors that map to a form field — the only ones shown to the
 * customer.
 *
 * @param {IPrequalificationFieldError[]} [errors] Backend field errors.
 * @return {IPrequalificationFieldError[]} Errors with a matching form field.
 */
export const getFormFieldErrors = (
  errors: IPrequalificationFieldError[] | null | undefined,
): IPrequalificationFieldError[] =>
  (errors ?? []).filter(({ field, messages }) => messages.length && SERVER_ERROR_FIELD_MAP[field]);

/**
 * Form fields an error should highlight inline. An on-file email is
 * read-only, so it's skipped.
 *
 * @param {string} field Backend error key.
 * @param {{ emailOnFile: boolean }} options Whether the email is locked.
 * @return {PrequalFormFieldName[]} Editable form fields for the key.
 */
export const getEditableFormFields = (
  field: string,
  { emailOnFile }: { emailOnFile: boolean },
): PrequalFormFieldName[] =>
  (SERVER_ERROR_FIELD_MAP[field] ?? []).filter(
    (formField) => formField !== "email" || !emailOnFile,
  );

/**
 * Whether the customer can fix at least one error by editing the form.
 * Otherwise sending them back would only resubmit the same data and fail
 * again.
 *
 * @param {IPrequalificationFieldError[]} errors Backend field errors.
 * @param {{ emailOnFile: boolean }} options Whether the email is locked.
 * @return {boolean} True when an error maps to an editable field.
 */
export const hasEditableFieldError = (
  errors: IPrequalificationFieldError[],
  options: { emailOnFile: boolean },
): boolean => errors.some(({ field }) => getEditableFormFields(field, options).length > 0);
